// src/components/ProposalCard.jsx
//
// Visual replacement for the deleted OfferCard. Renders a single proposal
// (multiple slots supported) with status pill, optional message, and the
// appropriate footer actions for the current viewer + status.
//
// Action callbacks (`onAccept`, `onDecline`, `onCancel`) are provided by the
// parent dashboard. They receive (proposalId, payload?) and are expected to
// drive proposalService themselves so the card stays presentational.
import React, { useState } from 'react';
import { Calendar, Clock, User } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  STATUS_LABEL,
  STATUS_ICON,
  STATUS_PILL_CLASS,
} from '../constants/proposalStatusConfig';
import styles from './ProposalCard.module.css';

function counterpartyName(proposal, viewerId) {
  const participants = proposal.participants || [];
  const other = participants.find(p => p.user_id !== viewerId) || participants[0];
  if (!other) return 'Unknown';
  return (
    other.full_name ||
    other.name ||
    other.email ||
    other.business_name ||
    'Unknown'
  );
}

function counterpartySubtext(proposal, viewerId) {
  const participants = proposal.participants || [];
  const other = participants.find(p => p.user_id !== viewerId) || participants[0];
  if (!other) return null;
  return other.business_name && other.business_name !== other.full_name
    ? other.business_name
    : other.email || null;
}

export default function ProposalCard({
  proposal,
  viewerId,
  onAccept,
  onDecline,
  onCancel,
}) {
  const [busy, setBusy] = useState(false);

  const status = proposal.status || 'proposed';
  const StatusIcon = STATUS_ICON[status] || STATUS_ICON.proposed;
  const pillClass = STATUS_PILL_CLASS[status] || STATUS_PILL_CLASS.proposed;
  const slots = proposal.slots || [];
  const selectedSlotId = proposal.selected_slot_id || null;

  const isOriginator = proposal.originator_id === viewerId;
  const isRecipient = !isOriginator;
  const canRespond = status === 'proposed' && isRecipient;
  const canCancel = status === 'accepted' && (isOriginator || isRecipient);

  const handleAccept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // First slot wins for v1 — multi-slot acceptance ships when the picker does.
      await onAccept?.(proposal.id, { selectedSlotId: slots[0]?.id });
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onDecline?.(proposal.id);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onCancel?.(proposal.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`card ${styles.card}`}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={`card-icon ${styles.iconBadge}`}>
            <User size={20} aria-hidden="true" />
          </div>
          <div>
            <h3 className={styles.name}>{counterpartyName(proposal, viewerId)}</h3>
            {counterpartySubtext(proposal, viewerId) && (
              <p className={styles.subtext}>{counterpartySubtext(proposal, viewerId)}</p>
            )}
          </div>
        </div>
        <span className={`pill ${pillClass} ${styles.pill}`}>
          <StatusIcon size={12} aria-hidden="true" />
          {STATUS_LABEL[status] || status}
        </span>
      </div>

      <ul className={styles.slotList}>
        {slots.map(slot => {
          const start = new Date(slot.start_time);
          const end = new Date(slot.end_time);
          const durationMin = Math.round((end - start) / (1000 * 60));
          const isSelected = selectedSlotId && slot.id === selectedSlotId;
          return (
            <li
              key={slot.id || slot.start_time}
              className={`${styles.slot} ${isSelected ? styles.slotSelected : ''}`}
            >
              <div className={styles.slotRow}>
                <Calendar size={14} className={styles.slotIcon} aria-hidden="true" />
                <span className={styles.slotDate}>
                  {format(start, 'EEE, MMM d, yyyy')}
                </span>
              </div>
              <div className={styles.slotRow}>
                <Clock size={14} className={styles.slotIcon} aria-hidden="true" />
                <span className={styles.slotTime}>
                  {format(start, 'h:mm a')} – {format(end, 'h:mm a')}
                  <span className={styles.duration}> ({durationMin} min)</span>
                </span>
                {isSelected && (
                  <span className={styles.selectedTag}>
                    {/* TODO(naming) confirmed-slot indicator */}
                    Confirmed slot
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {proposal.message && (
        <div className={styles.message}>"{proposal.message}"</div>
      )}

      <div className={styles.footer}>
        <p className={styles.timestamp}>
          {proposal.created_at && (
            <>Sent {formatDistanceToNow(new Date(proposal.created_at), { addSuffix: true })}</>
          )}
        </p>
        <div className={styles.actions}>
          {proposal.id && (
            <Link to={`/proposals/${proposal.id}`} className={styles.viewLink}>
              {/* TODO(naming) proposal detail link */}
              View
            </Link>
          )}
          {canRespond && (
            <>
              <button
                type="button"
                onClick={handleDecline}
                disabled={busy}
                className="btn"
              >
                {/* TODO(naming) recipient decline button */}
                Decline
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={busy}
                className="btn btn-primary"
              >
                {/* TODO(naming) recipient accept button */}
                {busy ? 'Working…' : 'Accept'}
              </button>
            </>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy}
              className="btn btn-danger"
            >
              {/* TODO(naming) cancel confirmed proposal */}
              {busy ? 'Working…' : 'Cancel meeting'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
